import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './DataPage.css';

import { API_URL } from '../config';
import { formatDate, formatTime } from '../utils/formatDate';

function ColetasDomiciliares() {
  const navigate = useNavigate();
  const [pendentesCount, setPendentesCount] = useState(0);

  useEffect(() => {
    const fetchPendentes = async () => {
      try {
        const { data } = await axios.get(`${API_URL}/agendamentos-coleta?status=pendente`);
        setPendentesCount(data.stats?.pendente || 0);
      } catch { /* silencioso */ }
    };
    fetchPendentes();
  }, []);

  // Calcular data padrão (últimos 30 dias)
  const hoje = new Date();
  const trintaDiasAtras = new Date();
  trintaDiasAtras.setDate(hoje.getDate() - 30);

  const [coletas, setColetas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState('calendario'); // 'tabela' ou 'calendario'
  const [weekOffset, setWeekOffset] = useState(0); // Deslocamento de semanas (0 = semana atual)
  const [selectedColeta, setSelectedColeta] = useState(null); // Coleta selecionada para o modal
  const [searchInput, setSearchInput] = useState('');
  const [dataInicio, setDataInicio] = useState(trintaDiasAtras.toISOString().split('T')[0]);
  const [dataFim, setDataFim] = useState(hoje.toISOString().split('T')[0]);
  const [usuario, setUsuario] = useState('');
  const [convenio, setConvenio] = useState('');
  const [sortBy, setSortBy] = useState('DATA');
  const [sortOrder, setSortOrder] = useState('DESC');
  const [limit, setLimit] = useState(100);
  const [stats, setStats] = useState({ total: 0, pacientesUnicos: 0, masculino: 0, feminino: 0, hoje: 0 });
  const [filters, setFilters] = useState({ usuarios: [], convenios: [] });

  const [fetchTrigger, setFetchTrigger] = useState(0);

  // Auto-fetch on sort/limit changes and manual triggers (also handles initial mount)
  useEffect(() => {
    fetchColetas();
  }, [sortBy, sortOrder, limit, fetchTrigger]);

  const handleSearch = () => {
    setFetchTrigger(prev => prev + 1);
  };

  const fetchColetas = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        limit: limit.toString(),
        sortBy,
        sortOrder
      });

      if (searchInput) params.append('search', searchInput);
      if (dataInicio) params.append('dataInicio', dataInicio);
      if (dataFim) params.append('dataFim', dataFim);
      if (usuario) params.append('usuario', usuario);
      if (convenio) params.append('convenio', convenio);

      const response = await axios.get(`${API_URL}/coletas-domiciliares?${params.toString()}`);
      setColetas(response.data.data || []);
      setStats(response.data.stats || { total: 0, pacientesUnicos: 0, masculino: 0, feminino: 0, hoje: 0 });
      setFilters(response.data.filters || { usuarios: [], convenios: [] });
    } catch (err) {
      console.error('Erro ao buscar coletas domiciliares:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'ASC' ? 'DESC' : 'ASC');
    } else {
      setSortBy(field);
      setSortOrder('ASC');
    }
  };

  const getSortIcon = (field) => {
    if (sortBy !== field) return '↕️';
    return sortOrder === 'ASC' ? '↑' : '↓';
  };

  // formatDate importado de ../utils/formatDate

  const formatDateWithWeekday = (dateString) => {
    if (!dateString) return '-';
    // Extrair YYYY-MM-DD da string ISO sem conversão de timezone
    const str = typeof dateString === 'string' ? dateString : new Date(dateString).toISOString();
    const datePart = str.includes('T') ? str.split('T')[0] : str;
    const [y, m, d] = datePart.split('-');
    if (!y || !m || !d) return formatDate(dateString);
    // Criar date ao meio-dia local para evitar shift de timezone
    const date = new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0);
    const weekdays = ['Domingo', 'Segunda', 'Terça', 'Quarta', 'Quinta', 'Sexta', 'Sábado'];
    const weekday = weekdays[date.getDay()];
    return `${weekday}, ${d}/${m}/${y}`;
  };

  // formatTime importado de ../utils/formatDate

  // Gerar dias da semana baseado no offset
  const getWeekDays = () => {
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0 = domingo
    const startOfWeek = new Date(today);
    startOfWeek.setDate(today.getDate() - dayOfWeek + (weekOffset * 7));

    const days = [];
    for (let i = 0; i < 7; i++) {
      const day = new Date(startOfWeek);
      day.setDate(startOfWeek.getDate() + i);
      // Usar partes locais para evitar shift de timezone com toISOString
      const y = day.getFullYear();
      const m = String(day.getMonth() + 1).padStart(2, '0');
      const d = String(day.getDate()).padStart(2, '0');
      days.push(`${y}-${m}-${d}`);
    }
    return days;
  };

  const weekDays = getWeekDays();

  // String de hoje usando partes locais (para comparação no calendário)
  const nowLocal = new Date();
  const todayStr = `${nowLocal.getFullYear()}-${String(nowLocal.getMonth() + 1).padStart(2, '0')}-${String(nowLocal.getDate()).padStart(2, '0')}`;

  // Agrupar coletas por dia
  const coletasPorDia = coletas.reduce((grupos, coleta) => {
    // Extrair YYYY-MM-DD direto da string sem new Date() para evitar timezone shift
    const str = typeof coleta.DATA === 'string' ? coleta.DATA : (coleta.DATA ? new Date(coleta.DATA).toISOString() : '');
    const dataKey = str.includes('T') ? str.split('T')[0] : str.substring(0, 10);
    if (!grupos[dataKey]) {
      grupos[dataKey] = [];
    }
    grupos[dataKey].push(coleta);
    return grupos;
  }, {});

  // Ordenar as datas
  const datasOrdenadas = Object.keys(coletasPorDia).sort((a, b) => {
    if (sortOrder === 'DESC') {
      return new Date(b) - new Date(a);
    }
    return new Date(a) - new Date(b);
  });

  const clearFilters = () => {
    setSearchInput('');
    setUsuario('');
    setConvenio('');
    const hoje = new Date();
    const trintaDiasAtras = new Date();
    trintaDiasAtras.setDate(hoje.getDate() - 30);
    setDataInicio(trintaDiasAtras.toISOString().split('T')[0]);
    setDataFim(hoje.toISOString().split('T')[0]);
    setFetchTrigger(prev => prev + 1);
  };

  if (loading && coletas.length === 0) {
    return <div className="loading">Carregando coletas domiciliares...</div>;
  }

  const styles = {
    pageHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '24px',
      flexWrap: 'wrap',
      gap: '12px',
    },
    pageIcon: {
      fontSize: '1.5rem',
      lineHeight: 1,
    },
    pageTitle: {
      fontSize: '1.5rem',
      fontWeight: '700',
      color: '#1a1a2e',
      margin: '0 0 2px 0',
    },
    pageSubtitle: {
      fontSize: '0.85rem',
      color: '#666',
      margin: 0,
    },
    btnPesquisar: {
      padding: '8px 20px',
      backgroundColor: '#2563eb',
      color: '#fff',
      border: 'none',
      borderRadius: '6px',
      cursor: 'pointer',
      fontSize: '0.85rem',
      fontWeight: '600',
      whiteSpace: 'nowrap',
    },
  };

  return (
    <div className="data-page">
      <div style={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <span style={styles.pageIcon}>🏠</span>
          <div>
            <h1 style={styles.pageTitle}>Coletas Domiciliares</h1>
            <p style={styles.pageSubtitle}>
              Exibindo {coletas.length} de {stats.total} coletas
              {(searchInput || dataInicio || dataFim || usuario || convenio) && ' (filtrado)'}
              {coletas.length === limit && stats.total > limit && ` - Limite de ${limit} atingido`}
            </p>
          </div>
        </div>
        {pendentesCount > 0 && (
          <button
            onClick={() => navigate('/agendamentos-coleta')}
            style={{ padding: '8px 16px', backgroundColor: '#f59e0b', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: '600' }}
          >
            Solicitações Pendentes ({pendentesCount})
          </button>
        )}
      </div>

      <div className="filters-section">
        <div className="search-bar" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Buscar por nome do paciente, CPF ou telefone... (pressione Enter)"
            value={searchInput}
            onChange={(e) => {
              setSearchInput(e.target.value);
              if (e.target.value) {
                setDataInicio('');
                setDataFim('');
              }
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                handleSearch();
              }
            }}
            className="search-input"
          />
          <button onClick={handleSearch} style={styles.btnPesquisar}>
            Pesquisar
          </button>
        </div>

        <div className="filters-row">
          <div className="filter-group">
            <label>Data Início:</label>
            <input
              type="date"
              value={dataInicio}
              onChange={(e) => setDataInicio(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Data Fim:</label>
            <input
              type="date"
              value={dataFim}
              onChange={(e) => setDataFim(e.target.value)}
              className="filter-select"
            />
          </div>

          <div className="filter-group">
            <label>Usuário:</label>
            <select
              value={usuario}
              onChange={(e) => setUsuario(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {filters.usuarios.map((u, idx) => (
                <option key={idx} value={u}>{u}</option>
              ))}
            </select>
          </div>

          <div className="filter-group">
            <label>Convênio:</label>
            <select
              value={convenio}
              onChange={(e) => setConvenio(e.target.value)}
              className="filter-select"
            >
              <option value="">Todos</option>
              {filters.convenios.map((c, idx) => (
                <option key={idx} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="filter-select"
          >
            <option value="50">50 registros</option>
            <option value="100">100 registros</option>
            <option value="200">200 registros</option>
            <option value="500">500 registros</option>
          </select>

          {(searchInput || usuario || convenio) && (
            <button onClick={clearFilters} className="clear-filters-btn">
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Resumo dos Dados Filtrados */}
      <div className="compact-stats">
        <div className="compact-stat-card teal">
          <div className="compact-stat-value">{(stats.total || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Total Coletas</div>
        </div>
        <div className="compact-stat-card blue">
          <div className="compact-stat-value">{(stats.pacientesUnicos || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Pacientes Únicos</div>
        </div>
        <div className="compact-stat-card cyan">
          <div className="compact-stat-value">{(stats.masculino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Masculino</div>
        </div>
        <div className="compact-stat-card pink">
          <div className="compact-stat-value">{(stats.feminino || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Feminino</div>
        </div>
        <div className="compact-stat-card green">
          <div className="compact-stat-value">{(stats.hoje || 0).toLocaleString('pt-BR')}</div>
          <div className="compact-stat-label">Hoje</div>
        </div>
      </div>

      {/* Botão de alternância de visualização */}
      <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', gap: '10px' }}>
        <button
          onClick={() => setViewMode('calendario')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'calendario' ? '#3b82f6' : 'white',
            color: viewMode === 'calendario' ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📅 Calendário
        </button>
        <button
          onClick={() => setViewMode('tabela')}
          style={{
            padding: '10px 20px',
            background: viewMode === 'tabela' ? '#3b82f6' : 'white',
            color: viewMode === 'tabela' ? 'white' : '#374151',
            border: '1px solid #d1d5db',
            borderRadius: '6px',
            cursor: 'pointer',
            fontWeight: '500'
          }}
        >
          📋 Lista
        </button>
      </div>

      {viewMode === 'tabela' ? (
        // Visualização em tabela por dia
        datasOrdenadas.map((dataKey) => (
        <div key={dataKey} style={{ marginBottom: '2rem' }}>
          {/* Cabeçalho do Dia */}
          <div style={{
            background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
            padding: '1rem 1.5rem',
            borderRadius: '8px 8px 0 0',
            color: 'white',
            fontSize: '1.1rem',
            fontWeight: 'bold',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}>
            <span>📅 {formatDateWithWeekday(dataKey)}</span>
            <span style={{
              background: 'rgba(255, 255, 255, 0.2)',
              padding: '0.25rem 0.75rem',
              borderRadius: '20px',
              fontSize: '0.9rem'
            }}>
              {coletasPorDia[dataKey].length} coleta{coletasPorDia[dataKey].length !== 1 ? 's' : ''}
            </span>
          </div>

          {/* Tabela de Coletas do Dia - Desktop */}
          <div className="table-card coletas-desktop" style={{ marginTop: 0, borderRadius: '0 0 8px 8px' }}>
            <div className="table-responsive">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Hora</th>
                    <th>Paciente</th>
                    <th>CPF</th>
                    <th>Sexo</th>
                    <th>Telefone</th>
                    <th>Endereço</th>
                    <th>Convênio</th>
                    <th>Usuário</th>
                    <th>Observação</th>
                  </tr>
                </thead>
                <tbody>
                  {coletasPorDia[dataKey].map((coleta) => (
                    <tr
                      key={coleta.ID}
                      onClick={() => navigate(`/coletas-domiciliares/${coleta.ID}`)}
                      style={{ cursor: 'pointer' }}
                      className="clickable-row"
                    >
                      <td>{coleta.ID}</td>
                      <td><strong>{formatTime(coleta.HORA)}</strong></td>
                      <td><strong>{coleta.PACIENTE || '-'}</strong></td>
                      <td>{coleta.CPF || '-'}</td>
                      <td>{coleta.SEXO || '-'}</td>
                      <td>{coleta.TELEFONE || coleta.CELULAR || '-'}</td>
                      <td style={{ maxWidth: '200px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {coleta.ENDERECO || '-'}
                      </td>
                      <td>{coleta.CONVENIO || '-'}</td>
                      <td>{coleta.USUARIO || '-'}</td>
                      <td style={{ maxWidth: '150px', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {coleta.OBSERVACAO || '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Cards de Coletas do Dia - Mobile */}
          <div className="coletas-mobile">
            {coletasPorDia[dataKey].map((coleta) => {
              const hora = formatTime(coleta.HORA);
              const cpf = coleta.CPF;
              const telefone = coleta.TELEFONE || coleta.CELULAR;
              const convenioColeta = coleta.CONVENIO;
              const endereco = coleta.ENDERECO;
              const observacao = coleta.OBSERVACAO;

              return (
                <div
                  className="coleta-card"
                  key={coleta.ID}
                  onClick={() => navigate(`/coletas-domiciliares/${coleta.ID}`)}
                  style={{ cursor: 'pointer' }}
                >
                  <div className="coleta-card-header">
                    <span className="coleta-card-id">#{coleta.ID}</span>
                    <span className="coleta-card-hora">{hora}</span>
                  </div>
                  <div className="coleta-card-paciente">{coleta.PACIENTE || '-'}</div>
                  <div className="coleta-card-meta">
                    {cpf && <span>CPF: {cpf}</span>}
                    {telefone && <span>Tel: {telefone}</span>}
                    {convenioColeta && <span>{convenioColeta}</span>}
                  </div>
                  {endereco && <div className="coleta-card-endereco">{endereco}</div>}
                  {observacao && <div className="coleta-card-obs">{observacao}</div>}
                </div>
              );
            })}
          </div>
        </div>
      ))
      ) : (
        // Visualização de calendário semanal
        <div style={{ background: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
          {/* Navegação de semanas */}
          <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '15px' }}>
            <button
              onClick={() => setWeekOffset(weekOffset - 1)}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              ← Semana Anterior
            </button>
            <div style={{ fontWeight: '600', color: '#374151', fontSize: '1rem' }}>
              {weekOffset === 0 ? 'Semana Atual' : weekOffset > 0 ? `${weekOffset} semana${weekOffset > 1 ? 's' : ''} à frente` : `${Math.abs(weekOffset)} semana${Math.abs(weekOffset) > 1 ? 's' : ''} atrás`}
            </div>
            {weekOffset !== 0 && (
              <button
                onClick={() => setWeekOffset(0)}
                style={{
                  padding: '8px 16px',
                  background: '#10b981',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Hoje
              </button>
            )}
            <button
              onClick={() => setWeekOffset(weekOffset + 1)}
              style={{
                padding: '8px 16px',
                background: '#3b82f6',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Próxima Semana →
            </button>
          </div>

          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '1200px' }}>
              <thead>
                <tr>
                  <th style={{
                    padding: '12px',
                    borderBottom: '2px solid #e5e7eb',
                    borderRight: '1px solid #e5e7eb',
                    background: '#f9fafb',
                    position: 'sticky',
                    left: 0,
                    zIndex: 10,
                    width: '80px',
                    fontWeight: '600',
                    color: '#374151'
                  }}>Hora</th>
                  {weekDays.map((dataKey) => {
                    // Parsear YYYY-MM-DD ao meio-dia local para evitar timezone shift
                    const [yw, mw, dw] = dataKey.split('-');
                    const date = new Date(parseInt(yw), parseInt(mw) - 1, parseInt(dw), 12, 0, 0);
                    const weekdays = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
                    const weekday = weekdays[date.getDay()];
                    const isToday = dataKey === todayStr;
                    return (
                      <th key={dataKey} style={{
                        padding: '12px',
                        borderBottom: '2px solid #e5e7eb',
                        borderRight: '1px solid #e5e7eb',
                        textAlign: 'center',
                        background: isToday ? '#dbeafe' : '#f9fafb',
                        fontWeight: '600',
                        color: '#374151',
                        width: '160px',
                        minWidth: '160px',
                        maxWidth: '160px'
                      }}>
                        <div>{weekday}</div>
                        <div style={{ fontSize: '0.75rem', marginTop: '4px', fontWeight: 'normal', color: '#6b7280' }}>
                          {dw}/{mw}
                        </div>
                        <div style={{ fontSize: '0.7rem', marginTop: '2px', fontWeight: 'normal', color: '#9ca3af' }}>
                          {coletasPorDia[dataKey]?.length || 0} coleta{(coletasPorDia[dataKey]?.length || 0) !== 1 ? 's' : ''}
                        </div>
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {Array.from({ length: 17 }, (_, i) => i + 6).map(hora => (
                  <tr key={hora}>
                    <td style={{
                      padding: '8px',
                      borderRight: '1px solid #e5e7eb',
                      borderBottom: '1px solid #e5e7eb',
                      background: '#f9fafb',
                      position: 'sticky',
                      left: 0,
                      zIndex: 9,
                      fontWeight: '500',
                      color: '#6b7280',
                      fontSize: '0.875rem'
                    }}>
                      {String(hora).padStart(2, '0')}:00
                    </td>
                    {weekDays.map((dataKey) => {
                      const coletasNaHora = (coletasPorDia[dataKey] || []).filter(coleta => {
                        if (!coleta.HORA) return false;

                        let horaColeta = null;
                        const horaStr = String(coleta.HORA);

                        // Se for timestamp ISO (1970-01-01T12:00) - extrair hora direto da string
                        if (horaStr.includes('T')) {
                          const timePart = horaStr.split('T')[1];
                          if (timePart) horaColeta = parseInt(timePart.split(':')[0]);
                        }
                        // Se for string no formato HH:MM
                        else if (horaStr.includes(':')) {
                          horaColeta = parseInt(horaStr.split(':')[0]);
                        }

                        return horaColeta === hora;
                      });
                      const isToday = dataKey === todayStr;

                      return (
                        <td key={dataKey} style={{
                          padding: '4px',
                          borderRight: '1px solid #e5e7eb',
                          borderBottom: '1px solid #e5e7eb',
                          verticalAlign: 'top',
                          background: isToday ? '#eff6ff' : 'white',
                          minHeight: '60px',
                          width: '160px',
                          minWidth: '160px',
                          maxWidth: '160px'
                        }}>
                          {coletasNaHora.map(coleta => (
                            <div
                              key={coleta.ID}
                              onClick={() => setSelectedColeta(coleta)}
                              style={{
                                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                                color: 'white',
                                padding: '6px',
                                marginBottom: '4px',
                                borderRadius: '4px',
                                fontSize: '0.75rem',
                                cursor: 'pointer',
                                boxShadow: '0 1px 2px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s',
                                wordWrap: 'break-word',
                                overflowWrap: 'break-word'
                              }}
                              onMouseOver={(e) => {
                                e.currentTarget.style.transform = 'translateY(-2px)';
                                e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.2)';
                              }}
                              onMouseOut={(e) => {
                                e.currentTarget.style.transform = 'translateY(0)';
                                e.currentTarget.style.boxShadow = '0 1px 2px rgba(0,0,0,0.1)';
                              }}
                            >
                              <div style={{ fontWeight: '600', marginBottom: '2px' }}>
                                {formatTime(coleta.HORA)}
                              </div>
                              <div style={{ fontSize: '0.7rem', opacity: 0.95, wordWrap: 'break-word' }}>
                                {coleta.PACIENTE_NOME?.substring(0, 25)}
                                {coleta.PACIENTE_NOME?.length > 25 ? '...' : ''}
                              </div>
                              <div style={{ fontSize: '0.65rem', opacity: 0.85, marginTop: '2px', wordWrap: 'break-word' }}>
                                📍 {coleta.ENDERECO?.substring(0, 30)}
                                {coleta.ENDERECO?.length > 30 ? '...' : ''}
                              </div>
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de Resumo */}
      {selectedColeta && (
        <div
          onClick={() => setSelectedColeta(null)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000,
            padding: '20px'
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflowY: 'auto',
              boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
            }}
          >
            {/* Cabeçalho do Modal */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, color: '#1f2937', fontSize: '1.5rem' }}>
                🏠 Resumo da Coleta
              </h2>
              <button
                onClick={() => setSelectedColeta(null)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#6b7280',
                  padding: '0',
                  width: '32px',
                  height: '32px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                ×
              </button>
            </div>

            {/* Informações da Coleta */}
            <div style={{ marginBottom: '25px' }}>
              <div style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                color: 'white',
                padding: '15px',
                borderRadius: '8px',
                marginBottom: '20px'
              }}>
                <div style={{ fontSize: '0.875rem', opacity: 0.9, marginBottom: '5px' }}>ID da Coleta</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 'bold' }}>#{selectedColeta.ID}</div>
              </div>

              <div style={{ display: 'grid', gap: '15px' }}>
                <div>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>📅 DATA E HORA</div>
                  <div style={{ fontSize: '1rem', color: '#1f2937' }}>
                    {formatDateWithWeekday(selectedColeta.DATA)} às {formatTime(selectedColeta.HORA)}
                  </div>
                </div>

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>👤 PACIENTE</div>
                  <div style={{ fontSize: '1rem', color: '#1f2937', fontWeight: '500' }}>
                    {selectedColeta.PACIENTE_NOME || '-'}
                  </div>
                </div>

                {selectedColeta.PACIENTE_CPF && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>📄 CPF</div>
                    <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedColeta.PACIENTE_CPF}</div>
                  </div>
                )}

                {selectedColeta.PACIENTE_SEXO && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>⚧ SEXO</div>
                    <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedColeta.PACIENTE_SEXO === 'M' ? 'Masculino' : 'Feminino'}</div>
                  </div>
                )}

                {selectedColeta.TELEFONE && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>📞 TELEFONE</div>
                    <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedColeta.TELEFONE}</div>
                  </div>
                )}

                <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
                  <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>📍 ENDEREÇO</div>
                  <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedColeta.ENDERECO || '-'}</div>
                </div>

                {selectedColeta.CONVENIO && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>🏥 CONVÊNIO</div>
                    <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedColeta.CONVENIO}</div>
                  </div>
                )}

                {selectedColeta.USUARIO && (
                  <div>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>👨‍💼 RESPONSÁVEL</div>
                    <div style={{ fontSize: '1rem', color: '#1f2937' }}>{selectedColeta.USUARIO}</div>
                  </div>
                )}

                {selectedColeta.OBSERVACAO && (
                  <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '15px' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', marginBottom: '4px', fontWeight: '600' }}>📝 OBSERVAÇÕES</div>
                    <div style={{
                      fontSize: '0.875rem',
                      color: '#1f2937',
                      background: '#f9fafb',
                      padding: '10px',
                      borderRadius: '6px',
                      whiteSpace: 'pre-wrap'
                    }}>
                      {selectedColeta.OBSERVACAO}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Botões de Ação */}
            <div style={{ display: 'flex', gap: '10px', marginTop: '25px' }}>
              <button
                onClick={() => {
                  navigate(`/coletas-domiciliares/${selectedColeta.ID}`);
                  setSelectedColeta(null);
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}
              >
                Ver Detalhes Completos
              </button>
              <button
                onClick={() => setSelectedColeta(null)}
                style={{
                  padding: '12px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontWeight: '600',
                  fontSize: '1rem'
                }}
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ColetasDomiciliares;
